import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogResult, PopupDialog } from '@app/Common';
import { ProvisionedApplicationsTypes } from '@app/Enums';
import { Tenant } from '@app/Models/tenant';
import { TenantEnvironmentDetails } from '@app/Models/tenant-environment-details.model';
import { TenantService } from '@app/Services';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/internal/operators/filter';
import { map } from 'rxjs/internal/operators/map';
import { OptionCategory } from '../../Models';
import { CopyConfigService } from '../../Services';
import { AsyncPipe } from '@angular/common';
import { JobStatusPlaceholderComponent } from '../../../../../Common/job-status-placeholder/job-status-placeholder.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatTooltip } from '@angular/material/tooltip';
import { MatCheckbox } from '@angular/material/checkbox';
import { NoDataPlaceholderComponent } from '../../../../../Common/no-data-placeholder/no-data-placeholder.component';

@Component({
    selector: 'app-copy-config-export-only',
    templateUrl: './copy-config-export-only.component.html',
    styleUrls: [
        './copy-config-export-only.component.scss'
    ],
    imports: [JobStatusPlaceholderComponent, MatButton, MatIcon, FormsModule, MatSelect, MatOption, MatTooltip, MatCheckbox, NoDataPlaceholderComponent, AsyncPipe]
})
export class CopyConfigExportOnlyComponent implements OnInit {
  @ViewChild('exportIdInput') exportIdInput: ElementRef;

  copyConfigInProgress$: Observable<boolean>;
  copyConfigFinalised$: Observable<boolean>;
  copyConfigJobId$: Observable<string>;
  copyFromTenantValue: string;
  copyFromTenant: Tenant;
  copyFromEnvironment$: Observable<string>;
  copyFromTenant$: Observable<Tenant>;

  copyButtonText = 'Copy';

  constructor(
    private tenantService: TenantService,
    private copyConfigService: CopyConfigService,
    private dialog: MatDialog
  ) {
    this.copyConfigInProgress$ = this.copyConfigService.copyConfigInProgress$.asObservable();
    this.copyConfigFinalised$ = this.copyConfigService.copyConfigFinalised$.asObservable();
    this.copyConfigJobId$ = this.copyConfigService.copyConfigJobId$.asObservable();
    this.copyFromTenant$ = this.copyConfigService.sourceTenant$.asObservable();
  }

  ngOnInit(): void {
    this.copyConfigService.loadDefaultOptionsCategories();

    this.setDefaultTenantFromValue();
    this.changeCopyFrom();
  }

  selectText(): void {
    this.exportIdInput.nativeElement.select();
  }

  copyText(): void {
    if (!navigator?.clipboard) {
      return;
    }
    navigator.clipboard.writeText(this.exportIdInput.nativeElement.value);
    this.copyButtonText = 'Copied';
    setTimeout(() => this.copyButtonText = 'Copy', 2000);
  }

  copyAnotherTenant(): void {
    location.reload();
  }

  isAllOptionsChecked(category: OptionCategory): boolean {
    if (category.options.length === 0) {
      return false;
    }
    return category.options.every(option => option.Checked);
  }

  isSomeOptionsChecked(category: OptionCategory): boolean {
    if (category.options.length === 0 || this.isAllOptionsChecked(category)) {
      return false;
    }
    return category.options.some(option => option.Checked);
  }

  setAllOptions(category: OptionCategory, checked: boolean): void {
    category.options.forEach(option => option.Checked = checked);
  }

  categoryHasOptions(category: OptionCategory): boolean {
    return category.options.length > 0;
  }

  isSelectAllButtonAvailable(category: OptionCategory): boolean {
    return this.categoryHasOptions(category) && category.allowToggleAllOptions;
  }

  getCopyFromTenantOptions(): Tenant[] {
    return this.tenantService.tenants.filter((tenant) =>
      tenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)
    );
  }

  getOptionsCategories(): OptionCategory[] {
    return this.copyConfigService.optionsCategories;
  }

  changeCopyFrom(): void {
    if (!this.copyFromTenantValue) {
      return;
    }

    this.copyFromTenant = this.getCopyFromTenantOptions().find(
      (tenant) => tenant.Value === this.copyFromTenantValue
    );
    this.copyFromEnvironment$ = this.getEnvironmentForTenant(
      this.copyFromTenantValue
    );

    this.copyConfigService.loadTenantModuleConfigOptionsExportOnly(this.copyFromTenantValue);
  }

  copyConfigConfirm(): void {
    const dialogRef =
      this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
    dialogRef.componentInstance.DialogTitle = 'Copy Configuration Export Only';
    dialogRef.componentInstance.DialogDescription =
      `Are you sure you want to Copy Config <b>Export Only</b> from Tenant <b>${this.copyFromTenantValue}</b>?`;
    dialogRef.componentInstance.DialogConfirmButtonText = 'Confirm';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && result.Result) {
        this.copyConfigService.copyConfigInProgress$.next(true);
        this.copyConfig();
      } else {
        this.copyConfigService.copyConfigInProgress$.next(false);
      }
    });
  }

  copyConfig(): void {
    this.copyConfigService.sourceTenant$.next(this.copyFromTenant);
    this.copyConfigService.copyConfigExportOnlyRequest(this.copyFromTenantValue)
      .subscribe((jobId) => {
        this.copyConfigService.copyConfigJobId$.next(jobId);
      });
  }

  isCopyConfigAvailable(): boolean {
    return !!this.copyFromTenantValue;
  }

  private getEnvironmentForTenant(tenantName: string): Observable<string> {
    return this.tenantService
      .getEnvironmentDetailsByTenant(tenantName)
      .pipe(
        filter((environemntDetails: TenantEnvironmentDetails | null) => environemntDetails !== null),
        map((environemntDetails: TenantEnvironmentDetails) =>
          environemntDetails.EnvironmentName
        )
      );
  }

  private setDefaultTenantFromValue(): void {
    if (!this.tenantService.selectedTenant) {
      return;
    }
    this.copyFromTenant = this.tenantService.selectedTenant;
    this.copyFromTenantValue = this.copyFromTenant.Value;
  }
}
